import { IMAGES } from "@/lib/siteData";
import EmblemMotif from "@/components/site/EmblemMotif";

export default function About() {
  return (
    <section
      id="about"
      className="py-24 md:py-32"
      data-testid="about-section"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        {/* --- Overline + Headline --- */}
        <div className="text-center mb-16 md:mb-20">
          <EmblemMotif size="sm" className="mb-6" />
          <p className="eyebrow" data-testid="about-overline">Our Story</p>
          <h2
            className="mt-4 font-headings text-3xl md:text-5xl text-primary leading-tight max-w-3xl mx-auto"
            data-testid="about-heading"
          >
            A vision born of devotion, built for generations.
          </h2>
        </div>

        {/* --- Founder portrait + intro --- */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mb-20 md:mb-28">
          <figure className="md:col-span-5" data-testid="about-founder-figure">
            <div className="relative max-w-sm mx-auto">
              <div className="absolute -inset-2 md:-inset-3 border border-primary/40" aria-hidden="true" />
              <div className="relative overflow-hidden bg-muted">
                <img
                  src={IMAGES.founderPortrait}
                  alt="Sri L. Manae Hanumantha Rao — Founder & Visionary"
                  className="w-full h-auto object-cover sepia-[0.15]"
                  loading="lazy"
                  data-testid="about-founder-image"
                />
              </div>
            </div>
            <figcaption
              className="mt-6 text-center font-ui text-[11px] uppercase tracking-[0.24em] text-primary"
              data-testid="about-founder-caption"
            >
              Sri L. Manae Hanumantha Rao
              <span className="block mt-1 normal-case tracking-normal font-body text-sm text-foreground/70">
                (1883 — 1967) · Founder &amp; Visionary
              </span>
            </figcaption>
          </figure>

          <div className="md:col-span-7 space-y-6 font-body text-base md:text-lg text-foreground/80 leading-relaxed">
            <p data-testid="about-paragraph-1">
              The story of Sri Bhavani Kalyana Mandira Trust begins with the vision of our founder,
              <em> Late Sri L. Manae Hanumantha Rao </em>and his three sons —
              H. Narasinga Rao Manae, H. Rama Rao Manae and H. Lakshman Rao Manae — and further
              continued by the Manae family to serve the underprivileged community &amp;
              environment for better living through Sri Bhavani Kalyana Mandira Charitable Trust.
            </p>
            <p data-testid="about-paragraph-2">
              Established his business firms in <strong className="text-foreground">1908</strong> —
              <em> Sri Bhavani Saw Mills </em>and <em>Manae Hanumantha Rao and Sons</em> — both
              firms ran thriving timber &amp; furniture manufacturing businesses serving our
              Military Engineering Service (MES) &amp; Public Works Department (PWD) contractors,
              through sheer determination and an unyielding commitment to quality by Manae
              Hanumantha Rao &amp; his three sons.
            </p>
          </div>
        </div>

        {/* --- The Birth of the Mandira --- */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center mb-20 md:mb-28">
          <div className="md:col-span-6 md:order-2">
            <div className="relative">
              <div className="absolute -inset-2 md:-inset-3 border border-secondary/50" aria-hidden="true" />
              <img
                src={IMAGES.venueEntrance}
                alt="Sri Bhavani Kalyana Mandira — venue entrance decorated with marigold and banana leaves"
                className="relative w-full h-[360px] md:h-[460px] object-cover img-hover"
                loading="lazy"
                data-testid="about-venue-image"
              />
            </div>
          </div>

          <div className="md:col-span-6 md:order-1">
            <p className="eyebrow" data-testid="about-birth-overline">Chapter Two</p>
            <h3
              className="mt-4 font-headings text-2xl md:text-4xl text-primary leading-snug"
              data-testid="about-birth-heading"
            >
              The Birth of Sri Bhavani Kalyana Mandira
            </h3>
            <div className="mt-6 space-y-5 font-body text-base md:text-lg text-foreground/80 leading-relaxed">
              <p data-testid="about-birth-paragraph-1">
                Over the next two years, the structure was meticulously built, driven by deep
                spirituality and devotion to Goddess Sri Amba Bhavani.
              </p>
              <p data-testid="about-birth-paragraph-2">
                On <strong className="text-foreground">February 2, 1960</strong>, Sri Bhavani
                Kalyana Mandira Trust officially opened its doors to the public. Most of the family
                members' weddings were also performed here under the blessings of Goddess Sri Amba
                Bhavani.
              </p>
            </div>
          </div>
        </div>

        {/* --- Looking to the Future --- */}
        <div className="max-w-3xl mx-auto text-center">
          <p className="eyebrow" data-testid="about-future-overline">Chapter Three</p>
          <h3
            className="mt-4 font-headings text-2xl md:text-4xl text-primary leading-snug"
            data-testid="about-future-heading"
          >
            Looking to the Future
          </h3>
          <div className="mt-8 space-y-5 font-body text-base md:text-lg text-foreground/80 leading-relaxed text-left md:text-center">
            <p data-testid="about-future-paragraph-1">
              Remaining steadfast to the 1964 charter while evolving for the modern era, the Trust
              continues to upgrade its infrastructure under professional legal and audit guidance.
              As we step into the future, we are expanding our horizons through modern
              infrastructural additions, open-air eco-friendly wedding destinations, and a
              progressive commitment to inclusivity.
            </p>
            <p
              className="font-headings italic text-xl md:text-2xl text-primary leading-snug pt-4"
              data-testid="about-future-quote"
            >
              Today, Sri Bhavani Kalyana Mandira stands not just as a marriage hall, but as a
              living monument to an ancestral promise — providing an elegant, traditional and
              auspicious haven where your family's historic moments can be beautifully celebrated.
            </p>
          </div>
        </div>

        {/* --- Mini stats strip --- */}
        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 text-center md:text-left">
          {[
            { k: "Est. 1960", v: "Six decades of celebration" },
            { k: "1964", v: "Charter of the Trust" },
            { k: "Open Daily", v: "10 AM – 7 PM" },
            { k: "Flexible", v: "Vendor policy" },
          ].map((stat, i) => (
            <div key={stat.k} data-testid={`about-stat-${i}`}>
              <p className="font-headings text-2xl md:text-3xl text-primary">{stat.k}</p>
              <p className="font-ui text-[11px] uppercase tracking-[0.22em] text-foreground/60 mt-1">
                {stat.v}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
